import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { EventPricingMatrix, EventSchedule, EventTimeSlot, EventType, Roster } from '../../../../../../../../models/event-type';
import { AccountService } from '../../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../../services/event-type/event-type.service';
import { dateStringFromISO8601, dayOfWeekStringFromISO8601, getRandomNegativeId } from '../../../../../../../../utils';
import { LocationService } from '../../../../../../../../services/location/location.service';
import { CapitanLocation, LocationArea } from '../../../../../../../../models/location';
import { EventScheduleRepetition, ScheduleType } from '../../../../../../../../models/event-type-enums';
import { TaxRate } from '../../../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../../../services/tax-rate/tax-rate.service';
import { Membership } from '../../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../../services/membership/membership.service';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../../models/membership-enums';

@Component({
  selector: 'app-event-type-edit-schedule-repeating',
  templateUrl: './event-type-edit-schedule-repeating.component.html',
  styleUrls: ['./event-type-edit-schedule-repeating.component.css']
})
export class EventTypeEditScheduleRepeatingComponent implements OnInit {
  @Input()
  eventType: EventType;

  @Input()
  pricingMatrices: EventPricingMatrix[];

  @Input()
  locations: CapitanLocation[];

  public isNewSchedule: boolean;
  public schedule: EventSchedule = {
    id: null,
    url: '',
    name: '',
    customer_facing_name: '',
    roster_id: null,
    roster_name: null,
    location_id: null,
    location_name: '',
    location_url: '',
    location_area_id: null,
    duration_minutes: undefined,
    capacity: undefined,
    start_date: '',
    rolling_days: undefined,
    booking_open_time: '00:00',
    end_date: '',
    pricing_matrix_id: null,
    tax_rate_id: null,
    tax_rate_name: null,
    benefit_membership_id: null,
    benefit_membership_name: null,
    benefit_membership_additional_days: 0,
    repetition: EventScheduleRepetition.weekly,
    instructor_ids: [],
    instructor_details: [],
    created_at: '',
    updated_at: '',
    time_slots_url: '',
    events_url: ''
  };
  public eventTimeSlots: EventTimeSlot[] = [];
  public selectedRosterLocationId: number | null = null;

  public locationAreaList: LocationArea[];
  public rosters: Roster[];
  public taxRateList: TaxRate[];
  public benefitMembershipList: Membership[];

  public loadingLocationAreas = true;
  public loadingRosters = false;
  public loadingTaxRates = false;
  public loadingBenefitMemberships = false;
  public loadingSchedule = false;
  public loadingSave = false;

  public eventScheduleNotFound = false;
  public errorMessage = '';
  public errors: any = {};
  public serverError: number = null;

  // Mappings from time slot IDs to feedback for that time slot.
  public eventTimeSlotsErrorMessage: {[key: number]: string} = {};
  public eventTimeSlotsErrors: {[key: number]: any} = {};

  public readonly EventScheduleRepetition = EventScheduleRepetition;
  public readonly ScheduleType = ScheduleType;
  public readonly dayOfWeekStringFromISO8601 = dayOfWeekStringFromISO8601;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private taxRateService: TaxRateService,
    private membershipService: MembershipService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewSchedule = !routeParams.hasOwnProperty('scheduleId');

    this.loadLocationAreas();
    if (this.eventType.schedule_type === ScheduleType.rolling) {
      this.loadRosters();
    } else {
      this.loadTaxRates();
    }
    if (this.eventType.schedule_type === ScheduleType.repeating) {
      this.loadBenefitMemberships();
    }

    // Get the schedule to edit, if an ID was provided.
    if (!this.isNewSchedule) {
      const scheduleIdInt = parseInt(routeParams.scheduleId, 10);
      if (isNaN(scheduleIdInt)) {
        this.eventScheduleNotFound = true;
        return;
      }

      this.loadSchedule(scheduleIdInt);
    } else {
      this.schedule.location_id = this.accountService.getMyAccount().default_gym_id;

      // If this event type has pricing matrices, set the pricing matrix field
      // to the first one initially.
      if (this.pricingMatrices.length > 0) {
        this.schedule.pricing_matrix_id = this.pricingMatrices[0].id;
      }
    }
  }

  public loadLocationAreas() {
    this.loadingLocationAreas = true;

    this.serverError = null;

    this.locationService
      .getLocationAreas(
        1,
        100,
        {}
        )
      .then(response => { this.locationAreaList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocationAreas = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  public benefitMembershipRetired(id: number | null): boolean {
    if (id === null || id === undefined) {
      return false;
    }
    return !this.benefitMembershipList ||
      !this.benefitMembershipList.some(membership => membership.id === id);
  }

  public loadBenefitMemberships() {
    this.loadingBenefitMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {
          status: MembershipStatus.active,
          purchase_type: MembershipPurchaseType.benefit,
        }
      )
      .then(response => {
        this.benefitMembershipList = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingBenefitMemberships = false; });
  }

  public loadSchedule(scheduleId: number) {
    this.loadingSchedule = true;

    this.eventTypeService
      .getEventSchedule(this.eventType.id, scheduleId)
      .then(schedule => {
        this.schedule = schedule;

        this.schedule.instructor_ids = schedule.instructor_details.map(i => i.id);

        if (!this.loadingRosters && (this.eventType.schedule_type === ScheduleType.rolling)) {
          this.setRosterLocationId();
        }

        return this.eventTypeService
          .getEventTimeSlots(
            this.eventType.id,
            scheduleId,
            1,
            100)
          .then(timeSlotResponse => { this.eventTimeSlots = timeSlotResponse.results; });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventScheduleNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSchedule = false; });
  }

  public isLoading() {
    return this.loadingSchedule || this.loadingSave;
  }

  public save() {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};

    if (this.schedule.start_date === '') {
      this.schedule.start_date = undefined;
    }
    if (this.schedule.end_date === '') {
      this.schedule.end_date = undefined;
    }

    this.schedule.instructor_ids = this.schedule.instructor_details.map(i => i.id);

    let scheduleSavePromise: Promise<EventSchedule>;
    if (this.isNewSchedule) {
      scheduleSavePromise = this.eventTypeService.addEventSchedule(
        this.eventType.id,
        this.schedule
      );
    } else {
      scheduleSavePromise = this.eventTypeService.updateEventSchedule(
        this.schedule
      );
    }

    scheduleSavePromise
      .then(savedSchedule => {
        this.schedule = savedSchedule;

        // This will resolve when all of the time slots API calls have
        // concluded, regardless of whether they succeeded (each call's
        // promise has its own error handling).
        return Promise.all(
          this.eventTimeSlots.map(timeSlot => {
            return this.saveTimeSlot(timeSlot);
          })
        )
          .then(() => {
            // Once all time slots are saved, sync the schedule's events.
            return this.eventTypeService
              .syncScheduleEvents(this.schedule.id)
              .then(() => {
                this.router.navigate(
                  ['/events/setup/edit/' + this.eventType.id.toString() + '/schedules']
                );
              })
              .catch(reason => {
                if (reason.status === 403) {
                  this.errorMessage = 'You do not have permission to modify this schedule.';
                } else {
                  this.serverError = reason.status;
                }
                return Promise.reject();
              });
          })
          .catch(() => {
            // Reject with a timeSlotFailed key so the schedule catch function
            // knows that this was a time slot error, not a schedule error.
            return Promise.reject({timeSlotFailed: true});
          });
      })
      .catch(reason => {
        // Do not handle time slot errors.
        if (reason.timeSlotFailed) {
          return;
        }

        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewSchedule) {
            this.errors = 'You do not have permission to add schedules.';
          } else {
            this.errors = 'You do not have permission to modify this schedule.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  /**
   * Save the given time slot using the API.
   *
   * @return A promise with resolves if the time slot was saved correctly and
   *   rejects if not.
   */
  public saveTimeSlot(timeSlot: EventTimeSlot): Promise<void> {
    this.eventTimeSlotsErrorMessage[timeSlot.id] = '';
    this.eventTimeSlotsErrors[timeSlot.id] = {};

    if (timeSlot.start_time === '') {
      timeSlot.start_time = undefined;
    }

    let timeSlotSubmitPromise: Promise<EventTimeSlot>;
    if (timeSlot.id < 0) {
      timeSlotSubmitPromise = this.eventTypeService
        .addEventTimeSlot(this.eventType.id, this.schedule.id, timeSlot);
    } else {
      timeSlotSubmitPromise = this.eventTypeService
        .updateEventTimeSlot(timeSlot);
    }

    return timeSlotSubmitPromise
      .then(updatedTimeSlot => {
        // Replace the old time slot with the updated time slot.
        const timeSlotList = this.eventTimeSlots;
        timeSlotList.splice(timeSlotList.indexOf(timeSlot), 1, updatedTimeSlot);
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.eventTimeSlotsErrors[timeSlot.id] = reason.error;
          this.eventTimeSlotsErrorMessage[timeSlot.id] = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (timeSlot.id < 0) {
            this.eventTimeSlotsErrorMessage[timeSlot.id] = 'You do not have permission to add time slots.';
          } else {
            this.eventTimeSlotsErrorMessage[timeSlot.id] = 'You do not have permission to modify this time slot.';
          }
        } else {
          this.serverError = reason.status;
        }

        return Promise.reject();
      });
  }

  /**
   * Add a new time slot.
   */
  public addNewTimeSlot() {
    this.eventTimeSlots.push({
      id: getRandomNegativeId(),
      url: '',
      start_time: '',
      is_available_sunday: false,
      is_available_monday: false,
      is_available_tuesday: false,
      is_available_wednesday: false,
      is_available_thursday: false,
      is_available_friday: false,
      is_available_saturday: false,
      created_at: '',
      updated_at: ''
    });
  }

  /**
   * Remove the given time slot.
   */
  public removeTimeSlot(timeSlot: EventTimeSlot) {
    // If the schedule has a positive ID, then it is present in the API and must
    // be deleted.
    if (timeSlot.id > 0) {
      this.eventTypeService.deleteEventTimeSlot(timeSlot);
    }

    const timeSlotList = this.eventTimeSlots;

    timeSlotList.splice(timeSlotList.indexOf(timeSlot), 1);
  }

  /**
   * Whether all days are available for the given time slot.
   */
  public allDaysAreAvailable(timeSlot: EventTimeSlot) {
    return (
      timeSlot.is_available_sunday &&
      timeSlot.is_available_monday &&
      timeSlot.is_available_tuesday &&
      timeSlot.is_available_wednesday &&
      timeSlot.is_available_thursday &&
      timeSlot.is_available_friday &&
      timeSlot.is_available_saturday
    );
  }

  /**
   * Set all days to be available or unavailable for the given time slot.
   */
  public setAllDaysAvailability(timeSlot: EventTimeSlot, isAvailable: boolean) {
    timeSlot.is_available_sunday = isAvailable;
    timeSlot.is_available_monday = isAvailable;
    timeSlot.is_available_tuesday = isAvailable;
    timeSlot.is_available_wednesday = isAvailable;
    timeSlot.is_available_thursday = isAvailable;
    timeSlot.is_available_friday = isAvailable;
    timeSlot.is_available_saturday = isAvailable;
  }

  public getLocationAreasForLocation(): LocationArea[] {
    if (this.loadingLocationAreas) { return []; }

    return this.locationAreaList.filter(locationArea => locationArea.gym_id === this.schedule.location_id);
  }

  public setLocationToRosterDefault() {
    this.setRosterLocationId();
    if (this.selectedRosterLocationId) {
      this.schedule.location_id = this.selectedRosterLocationId;
    }
  }

  public getEventTypeTaxRate(): string {
    if (this.eventType.tax_rate_name) {
      let taxRateText = this.eventType.tax_rate_name;
      if (this.taxRateList) {
        for (const taxRate of this.taxRateList) {
          if (taxRate.id === this.eventType.tax_rate_id) {
            taxRateText += ' - ' + taxRate.rate_percent + '%';
          }
        }
      }
      return taxRateText;
    }
    else { return 'No Tax Rate'; }
  }

  private loadRosters() {
    this.loadingRosters = true;

    this.serverError = null;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: this.eventType.id}
        )
      .then(response => {
        this.rosters = response.results;
        if (!this.loadingSchedule && (this.eventType.schedule_type === ScheduleType.rolling)) {
          this.setRosterLocationId();
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }

  private setRosterLocationId() {
    for (const roster of this.rosters) {
      if (roster.id === this.schedule.roster_id) {
        this.selectedRosterLocationId = roster.location_id;
        return;
      }
    }
    this.selectedRosterLocationId = null;
  }
}
